import { CurrencyPipe, DecimalPipe, PercentPipe } from '@angular/common'
import { HttpParams } from '@angular/common/http'
import { Component } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { WjChartModule } from '@grapecity/wijmo.angular2.chart'
import { WjGridModule } from '@grapecity/wijmo.angular2.grid'
import { WjGridFilterModule } from '@grapecity/wijmo.angular2.grid.filter'
import { WjInputModule } from '@grapecity/wijmo.angular2.input'

import { BaseComponent, Grid, ListItem } from '../../../shared'
import { ActionToolbarCount } from '../../../shared/reusable-components/action-toolbar-count/action-toolbar-count.component'
import { ActionToolbarSearchComponent } from '../../../shared/reusable-components/action-toolbar-search/action-toolbar-search.component'
import { GridExportComponent } from '../../../shared/reusable-components/grid-export/grid-export.component'
import { GridSelectionCalculationComponent } from '../../../shared/reusable-components/grid-selection-calculation/grid-selection-calculation.component'

interface EquipmentYear {
    year: number
    hours: number
    cost: number
    rate: number | null
    fuelRate: number | null
    maintenanceRate: number | null
    operatingRate: number | null
    purchaseRate: number | null
    indirectRate: number | null
    ownershipRate: number | null
    unassignedRate: number | null
    unassignedCost: number
    unassignedCodes: { code: string; cost: number; rate: number | null }[]
}

interface EquipmentNode {
    id: string
    name: string
    level: string
    average: EquipmentYear | null
    years: EquipmentYear[]
    children: EquipmentNode[] | null
    searchText: string
    [key: string]: any
}

interface EquipmentCategoryRow {
    id: string
    category: string
    name: string
    hours: number
    averageRate: number | null
    fuelRate: number | null
    maintenanceRate: number | null
    purchaseRate: number | null
    indirectRate: number | null
    fuelGallonsPerHour: number | null
    trend: number | null
    adjustedFuel: number | null
    adjustedTotal: number | null
    yearRates: Record<number, number | null>
}

interface BreakdownLine {
    label: string
    bold: boolean
    indent: boolean
    kind: 'rate' | 'hours' | 'cost'
    average: number | null
    values: (number | null)[]
}

interface ChartSeries {
    binding: string
    name: string
}

@Component({
    selector: 'equipment-rates',
    templateUrl: './equipment-rates.component.html',
    standalone: true,
    imports: [
        FormsModule,
        CurrencyPipe,
        DecimalPipe,
        PercentPipe,
        WjInputModule,
        WjChartModule,
        WjGridModule,
        WjGridFilterModule,
        ActionToolbarCount,
        ActionToolbarSearchComponent,
        GridExportComponent,
        GridSelectionCalculationComponent,
    ],
})
export class EquipmentRatesComponent extends BaseComponent {
    readonly currentYear = new Date().getFullYear()
    startYear = this.currentYear - 5
    endYear = this.currentYear
    yearOptions: ListItem<number>[] = []
    years: number[] = []
    nodes: EquipmentNode[] = []
    categoryRows: EquipmentCategoryRow[] = []
    categoryOptions: ListItem<string>[] = []
    selected: EquipmentNode | null = null
    closedThrough = ''
    rangeInvalid = false
    loadError = ''
    fuelPrice: number | null = null
    trendPoints: Record<string, string | number | null>[] = []
    trendSeries: ChartSeries[] = []
    grid: Grid<EquipmentNode>
    categoryGrid: Grid<EquipmentCategoryRow>

    private categorySelectionReady = false

    constructor() {
        super()
        this.yearOptions = this.buildYearOptions()
        this.grid = new Grid<EquipmentNode>(this.services, 'id', this)
            .filterBy(item => [item.name, item.searchText])
            .wrapColumnHeaders()
        this.categoryGrid = new Grid<EquipmentCategoryRow>(this.services, 'id', this).filterBy(item => [item.name, item.category]).wrapColumnHeaders()
        this.grid.selectionChanged.on(() => this.onGridSelection())
        this.load()
    }

    onRangeChanged() {
        this.rangeInvalid = this.startYear > this.endYear
        if (this.rangeInvalid) return
        this.load()
    }

    onFuelPrice(value: number | null) {
        this.fuelPrice = value
        this.applyFuelPrice()
    }

    load() {
        this.loadError = ''
        const params = new HttpParams().set('startYear', this.startYear).set('endYear', this.endYear)
        this.cancelableRequest(
            'equipmentRates',
            true,
            this.services.data.load(true, true, this.services.data.get('/api/BidRate/GetEquipmentRates', params)).subscribe({
                next: result => {
                    const payload = result[0] as { years: number[]; nodes: any[]; categories: any[]; closedThrough: string }
                    this.closedThrough = payload.closedThrough ?? ''
                    this.years = payload.years ?? []
                    this.nodes = (payload.nodes ?? []).map(node => this.mapNode(node))
                    this.grid.items = this.nodes
                    this.selected = this.nodes[0] ?? null
                    this.mapCategories(payload.categories ?? [])
                    setTimeout(() => this.grid.flexGrid?.collapseGroupsToLevel(0), 0)
                    this.initialLoadingComplete()
                },
                error: () => {
                    this.loadError = 'Equipment rates could not be loaded.'
                    this.initialLoadingComplete()
                },
            }),
        )
    }

    onGridSelection() {
        const flex = this.grid.flexGrid
        if (!flex || flex.selection.row < 0 || flex.selection.row >= flex.rows.length) return
        const item = flex.rows[flex.selection.row]?.dataItem as EquipmentNode | undefined
        if (item) this.selected = item
    }

    get closedLabel(): string {
        return this.monthLabel(this.closedThrough)
    }

    get hasFuelPrice(): boolean {
        return this.fuelPrice != null && this.fuelPrice > 0
    }

    get breakdownLines(): BreakdownLine[] {
        const years = this.selected?.years ?? []
        const average = this.selected?.average ?? null
        const lines: BreakdownLine[] = [
            this.rateLine('Operating (EOE)', true, false, average, years, year => year.operatingRate),
            this.rateLine('Fuel (300)', false, true, average, years, year => year.fuelRate),
            this.rateLine('Maintenance & Repair (400)', false, true, average, years, year => year.maintenanceRate),
            this.rateLine('Ownership (Rent)', true, false, average, years, year => year.ownershipRate),
            this.rateLine('Purchase (100)', false, true, average, years, year => year.purchaseRate),
            this.rateLine('Indirects (200)', false, true, average, years, year => year.indirectRate),
        ]

        const unassignedCodes = this.unassignedCodes(years, average)
        if (unassignedCodes.length > 0 || years.some(year => year.unassignedCost) || average?.unassignedCost) {
            lines.push(this.rateLine('Unassigned', true, false, average, years, year => year.unassignedRate))
            for (const code of unassignedCodes) {
                lines.push(this.rateLine(code, false, true, average, years, year => this.codeRate(year, code)))
            }
        }

        lines.push(this.rateLine('Total Rate', true, false, average, years, year => year.rate))
        return lines
    }

    get supportLines(): BreakdownLine[] {
        const years = this.selected?.years ?? []
        const average = this.selected?.average ?? null
        return [
            {
                label: 'Hours',
                bold: false,
                indent: false,
                kind: 'hours',
                average: average?.hours ?? null,
                values: years.map(year => year.hours),
            },
            {
                label: 'Cost',
                bold: false,
                indent: false,
                kind: 'cost',
                average: average?.cost ?? null,
                values: years.map(year => year.cost),
            },
        ]
    }

    private buildYearOptions(): ListItem<number>[] {
        const options: ListItem<number>[] = []
        for (let year = this.currentYear; year >= 2012; year--) options.push(new ListItem(year, String(year)))
        return options
    }

    private mapNode(node: any): EquipmentNode {
        const children = ((node.children ?? []) as any[]).map(child => this.mapNode(child))
        const average = (node.average ?? null) as EquipmentYear | null
        const mapped: EquipmentNode = {
            id: node.id,
            name: node.name,
            level: node.level,
            average,
            years: node.years ?? [],
            children: children.length ? children : null,
            searchText: '',
        }
        mapped['avgRate'] = average?.rate ?? null
        mapped['avgHours'] = average?.hours ?? null
        mapped['avgCost'] = average?.cost ?? null
        for (const year of mapped.years) {
            mapped['rate' + year.year] = year.rate
            mapped['hours' + year.year] = year.hours
            mapped['cost' + year.year] = year.cost
        }
        mapped.searchText = [mapped.name, ...children.map(child => child.searchText)].join(' ')
        return mapped
    }

    private mapCategories(categories: any[]) {
        const newest = this.years.length ? Math.max(...this.years) : 0
        const oldest = this.years.length ? Math.min(...this.years) : 0
        this.categoryRows = categories.map(row => {
            const yearRates: Record<number, number | null> = {}
            for (const year of row.years ?? []) yearRates[year.year] = year.rate ?? null
            const oldestRate = yearRates[oldest]
            const newestRate = yearRates[newest]
            const trend = oldestRate != null && oldestRate !== 0 && newestRate != null ? (newestRate - oldestRate) / oldestRate : null
            return {
                id: row.category,
                category: row.category,
                name: row.name,
                hours: row.hours ?? 0,
                averageRate: row.averageRate ?? null,
                fuelRate: row.fuelRate ?? null,
                maintenanceRate: row.maintenanceRate ?? null,
                purchaseRate: row.purchaseRate ?? null,
                indirectRate: row.indirectRate ?? null,
                fuelGallonsPerHour: row.fuelGallonsPerHour ?? null,
                trend,
                adjustedFuel: null,
                adjustedTotal: null,
                yearRates,
            }
        })
        this.syncCategoryOptions()
        this.applyFuelPrice()
        this.rebuildTrend()
    }

    private syncCategoryOptions() {
        const previous = new Map(this.categoryOptions.map(item => [item.value, item.$checked]))
        const defaults = new Set(
            [...this.categoryRows]
                .sort((a, b) => b.hours - a.hours || a.name.localeCompare(b.name))
                .slice(0, 5)
                .map(row => row.category),
        )
        this.categoryOptions = [...this.categoryRows]
            .sort((a, b) => a.name.localeCompare(b.name))
            .map(row => {
                const item = new ListItem(row.category, row.name)
                item.$checked = this.categorySelectionReady ? (previous.get(row.category) ?? false) : defaults.has(row.category)
                return item
            })
        this.categorySelectionReady = true
    }

    rebuildTrend() {
        const selected = this.categoryOptions.filter(item => item.$checked)
        this.trendSeries = selected.map((item, index) => ({ binding: 'c' + index, name: item.display }))
        const years = [...this.years].sort((a, b) => a - b)
        this.trendPoints = years.map(year => {
            const point: Record<string, string | number | null> = { year: String(year) }
            selected.forEach((item, index) => {
                point['c' + index] = this.categoryRows.find(row => row.category === item.value)?.yearRates[year] ?? null
            })
            return point
        })
    }

    private applyFuelPrice() {
        const price = this.fuelPrice
        const usePrice = price != null && price > 0
        for (const row of this.categoryRows) {
            if (!usePrice || row.fuelGallonsPerHour == null || row.averageRate == null) {
                row.adjustedFuel = null
                row.adjustedTotal = null
                continue
            }
            row.adjustedFuel = row.fuelGallonsPerHour * price
            row.adjustedTotal = row.averageRate - (row.fuelRate ?? 0) + row.adjustedFuel
        }
        this.categoryGrid.items = this.categoryRows
        this.categoryGrid.view.refresh()
    }

    private rateLine(
        label: string,
        bold: boolean,
        indent: boolean,
        average: EquipmentYear | null,
        years: EquipmentYear[],
        value: (year: EquipmentYear) => number | null,
    ): BreakdownLine {
        return { label, bold, indent, kind: 'rate', average: average ? value(average) : null, values: years.map(value) }
    }

    private unassignedCodes(years: EquipmentYear[], average: EquipmentYear | null): string[] {
        const codes = new Set<string>()
        for (const year of [...years, average].filter((year): year is EquipmentYear => !!year)) {
            for (const code of year.unassignedCodes ?? []) codes.add(code.code)
        }
        return [...codes].sort((a, b) => a.localeCompare(b))
    }

    private codeRate(year: EquipmentYear, code: string): number | null {
        return (year.unassignedCodes ?? []).find(item => item.code === code)?.rate ?? null
    }

    private monthLabel(value: string | Date | null | undefined): string {
        const date = this.toMonthDate(value)
        if (!date) return ''
        return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
    }

    private toMonthDate(value: string | Date | null | undefined): Date | null {
        if (!value) return null
        if (value instanceof Date) {
            return isNaN(value.getTime()) ? null : new Date(value.getFullYear(), value.getMonth(), 1)
        }
        if (value.length < 7) return null
        const [year, month] = value.substring(0, 7).split('-').map(part => Number(part))
        if (!year || !month) return null
        return new Date(year, month - 1, 1)
    }
}
